import { Form, Formik, FormikHelpers } from 'formik';
import { FC } from 'react';

import { AuthFormHeader } from 'src/authentication/sections/AuthFormHeader/AuthFormHeader';
import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { Button } from 'src/general/components/Button/Button';
import { FormikInput } from 'src/general/components/forms/FormikInput/FormikInput';
import { FormikOption } from 'src/general/components/forms/FormikInput/FormikInput.types';
import { FormikInputPassword } from 'src/general/components/forms/FormikInput/FormikInputPassword';
import { FormikOnErrorTracking } from 'src/general/components/forms/FormikOnErrorTracking/FormikOnErrorTracking';
import { InputCheckboxFormik } from 'src/general/components/forms/Input/InputCheckboxFormik';
import { getMicrocopy } from 'src/stores/microcopyStore';
import { encodeHTMLEntities } from 'src/utils/encodeHTMLEntities';

import classes from './Forms.module.scss';
import { FormProps, SignupPersonalDetailsFormValidation, SignupPersonalDetailsFormValues } from './Forms.types';

interface Props extends FormProps<SignupPersonalDetailsFormValues> {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  newPassword?: string;
  marketing?: boolean;
}

const initialValues: SignupPersonalDetailsFormValues = {
  firstName: '',
  lastName: '',
  phone: '',
  newPassword: '',
  marketing: 'marketing',
};

const options: FormikOption[] = [{ value: 'marketing', label: 'I want to receive news, offers and updates' }];

export const SignupPersonalDetailsForm: FC<Props> = ({
  email,
  onBack,
  onSubmit,
  onSubmitTracking,
  firstName,
  lastName,
  phone,
  newPassword,
  marketing,
}) => (
  <>
    <AuthFormHeader
      title={getMicrocopy(MICROCOPY.AUTHENTICATION_SIGNUP_PERSONAL_DETAILS_FORM_TITLE)}
      subtitle={getMicrocopy(MICROCOPY.AUTHENTICATION_SIGNUP_PERSONAL_DETAILS_FORM_CAPTION, true, {
        email: encodeHTMLEntities(email),
      })}
      onBack={onBack}
    />
    <Formik
      initialValues={{
        ...initialValues,
        firstName: firstName ?? initialValues.firstName,
        lastName: lastName ?? initialValues.lastName,
        phone: phone ?? initialValues.phone,
        newPassword: newPassword ?? initialValues.newPassword,
        marketing: marketing ? initialValues.marketing : [],
      }}
      onSubmit={(
        values: SignupPersonalDetailsFormValues,
        formikHelpers: FormikHelpers<SignupPersonalDetailsFormValues>,
      ) => onSubmit({ ...values, marketing: values.marketing.includes('marketing') }, formikHelpers)}
      validationSchema={SignupPersonalDetailsFormValidation}
    >
      {(props) => (
        <Form className={classes.form} noValidate>
          <input type="hidden" name="email" value={email} />
          {!!onSubmitTracking && <FormikOnErrorTracking onError={onSubmitTracking} />}
          <div className={classes.signupPersonalDetailsForm}>
            <FormikInput
              type="text"
              name="firstName"
              label="First name"
              disabled={props.isSubmitting}
              autoFocus
              className={classes.field}
            />
            <FormikInput
              type="text"
              name="lastName"
              label="Last name"
              disabled={props.isSubmitting}
              className={classes.field}
            />
            <FormikInput
              type="tel"
              name="phone"
              label="Mobile number"
              maskType="mobile"
              disabled={props.isSubmitting}
              autoComplete="tel"
              className={classes.field}
            />
            <FormikInputPassword
              name="newPassword"
              label="Create a password"
              withHelpText
              disabled={props.isSubmitting}
              autoComplete="new-password"
              className={classes.field}
            />

            <div className={classes.checkboxContainer}>
              <InputCheckboxFormik
                id="marketing"
                name="marketing"
                options={options.map((item) => ({
                  value: item.value,
                  label: item.label,
                }))}
              />
            </div>
            <div className={classes.terms}>
              {getMicrocopy(MICROCOPY.PERSONALIZED_QUOTE_SIGNUP_TERM_AND_POLICY, true)}
            </div>
            <footer className={classes.formFooter}>
              <Button
                type="submit"
                isLoading={props.isSubmitting}
                disabled={props.isSubmitting}
                fullWidth
                data-testid="submit"
              >
                Create account
              </Button>
            </footer>
          </div>
        </Form>
      )}
    </Formik>
  </>
);
